import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ArrowBack as BackIcon, Save as SaveIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { CATEGORIAS_SAIDA_CAIXA, CategoriaSaidaCaixa } from '../../types';
import recurringCashMovementService, { RecurringCashMovement } from '../../services/recurringCashMovementService';

type Mode = 'mensal' | 'parcelado';

const RecurringCashMovementForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('mensal');

  const [form, setForm] = useState<Partial<RecurringCashMovement>>({
    type: 'saida',
    category: CATEGORIAS_SAIDA_CAIXA[0].value,
    description: '',
    amount: 0,
    payable_note: '',
    frequency: 'monthly',
    start_date: today,
    end_date: null,
    installments_total: null,
    status: 'active',
  });

  useEffect(() => {
    if (!isEditing) return;
    setLoading(true);
    recurringCashMovementService
      .get(Number(id))
      .then((data) => {
        setForm(data);
        setMode(data.installments_total ? 'parcelado' : 'mensal');
      })
      .catch((e) => {
        console.error(e);
        setError('Não foi possível carregar a recorrência.');
      })
      .finally(() => setLoading(false));
  }, [id, isEditing]);

  const handleSave = async () => {
    if (!form.description?.trim()) return;
    if (!form.amount || Number(form.amount) <= 0) return;
    if (!form.start_date) return;
    if (mode === 'parcelado' && (!form.installments_total || Number(form.installments_total) <= 0)) return;

    setSaving(true);
    setError(null);
    try {
      const payload: Partial<RecurringCashMovement> = {
        ...form,
        type: 'saida',
        frequency: 'monthly',
        installments_total: mode === 'parcelado' ? Number(form.installments_total) : null,
        end_date: mode === 'mensal' ? (form.end_date || null) : null,
      };
      if (isEditing) {
        await recurringCashMovementService.update(Number(id), payload);
      } else {
        await recurringCashMovementService.create(payload);
      }
      navigate('/movimentacoes/recorrentes');
    } catch (e) {
      console.error(e);
      setError('Não foi possível salvar.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/movimentacoes/recorrentes')} color="inherit">
          Voltar
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {isEditing ? 'Editar recorrência' : 'Nova recorrência'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Categoria</InputLabel>
              <Select
                value={(form.category || CATEGORIAS_SAIDA_CAIXA[0].value) as any}
                label="Categoria"
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value as CategoriaSaidaCaixa }))}
              >
                {CATEGORIAS_SAIDA_CAIXA.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Valor (R$)"
              value={String(form.amount ?? '')}
              onChange={(e) => setForm((p) => ({ ...p, amount: Number(e.target.value) }))}
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Descrição"
              value={form.description ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              helperText="Ex.: Boleto internet, Mensalidade sistema, Parcela empréstimo"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Início (primeiro vencimento)"
              type="date"
              value={form.start_date ?? today}
              onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select value={mode} label="Tipo" onChange={(e) => setMode(e.target.value as Mode)}>
                <MenuItem value="mensal">Mensal</MenuItem>
                <MenuItem value="parcelado">Parcelado (N vezes)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {mode === 'mensal' ? (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Fim (opcional)"
                type="date"
                value={(form.end_date as any) ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value || null }))}
                InputLabelProps={{ shrink: true }}
                helperText="Se definido, para de gerar após esta data"
              />
            </Grid>
          ) : (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Total de parcelas"
                type="number"
                value={form.installments_total ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, installments_total: Number(e.target.value) }))}
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Lembrete (opcional)"
              value={(form.payable_note as any) ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, payable_note: e.target.value }))}
              multiline
              rows={2}
              placeholder="Ex.: pagar no banco X, linha digitável, pix, etc."
            />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => navigate('/movimentacoes/recorrentes')} color="inherit">
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => void handleSave()}
              disabled={saving || !form.description?.trim() || !form.amount || Number(form.amount) <= 0}
            >
              Salvar
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default RecurringCashMovementForm;

